import { supabase } from './supabase';
import {
  DOORSTEPS_TIERS,
  type DoorstepsTier,
  type DoorstepsBillingCycle,
  getDoorstepsTier,
  getDoorstepsLetterRatePence,
} from '@rehub/pricing';

export {
  DOORSTEPS_TIERS,
  getDoorstepsTier,
  getDoorstepsLetterRatePence,
  type DoorstepsTier,
  type DoorstepsBillingCycle,
};

export interface DoorstepsSubscription {
  tier: DoorstepsTier;
  billing_cycle: DoorstepsBillingCycle | null;
  status: string | null;
  renews_at: string | null;
  cancels_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export async function fetchSubscription(userId: string): Promise<DoorstepsSubscription> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'doorsteps_subscription_tier, doorsteps_billing_cycle, doorsteps_subscription_status, doorsteps_subscription_renews_at, doorsteps_subscription_cancels_at, doorsteps_stripe_customer_id, doorsteps_stripe_subscription_id'
    )
    .eq('id', userId)
    .maybeSingle();
  if (error || !data) {
    return {
      tier: 'free',
      billing_cycle: null,
      status: null,
      renews_at: null,
      cancels_at: null,
      stripe_customer_id: null,
      stripe_subscription_id: null,
    };
  }
  return {
    tier: (data.doorsteps_subscription_tier as DoorstepsTier) ?? 'free',
    billing_cycle: data.doorsteps_billing_cycle as DoorstepsBillingCycle | null,
    status: data.doorsteps_subscription_status,
    renews_at: data.doorsteps_subscription_renews_at,
    cancels_at: data.doorsteps_subscription_cancels_at,
    stripe_customer_id: data.doorsteps_stripe_customer_id,
    stripe_subscription_id: data.doorsteps_stripe_subscription_id,
  };
}

export function formatPence(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

export function formatPounds(amount: number): string {
  return `£${amount.toLocaleString('en-GB')}`;
}

/**
 * Cost the agent will see for a campaign of N letters at their current tier.
 * Free tier returns 0 (UI prevents send anyway).
 */
export function estimateCampaignCostPence(targetCount: number, tier: DoorstepsTier): number {
  return targetCount * getDoorstepsLetterRatePence(tier);
}

/**
 * Open a Stripe Checkout Session for the chosen tier. Resolves with the
 * Checkout URL the client should redirect to.
 */
export async function createCheckoutSession(
  tier: Exclude<DoorstepsTier, 'free'>,
  cycle: DoorstepsBillingCycle
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('doorsteps-create-checkout-session', {
    body: { tier, cycle },
  });
  if (error) throw new Error(error.message || 'Checkout failed');
  if (!data?.url) throw new Error(data?.error || 'No checkout URL returned');
  return data.url as string;
}

/**
 * Open the Stripe customer billing portal so the agent can manage card,
 * invoices, or cancel.
 */
export async function openBillingPortal(): Promise<string> {
  const { data, error } = await supabase.functions.invoke('doorsteps-create-checkout-session', {
    body: { portal: true },
  });
  if (error) throw new Error(error.message || 'Could not open billing portal');
  if (!data?.url) throw new Error(data?.error || 'No portal URL returned');
  return data.url as string;
}
